import type { Point2D } from '@shared/models/geometry.model';
import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';

/** Sticky lock for soft direction snap across drag frames. */
export type StickyDirectionSnapState = {
  /** Locked bidirectional direction in `[0, π)`, when engaged. */
  lockedAngleRadians?: number;
};

export type SoftSnapOffsetToDirectionsResult = {
  position: Point2D;
  snapped: boolean;
  angleRadians?: number;
  state: StickyDirectionSnapState;
};

type SoftSnapOffsetToDirectionsOptions = {
  enterDistanceMeters: number;
  breakoutDistanceMeters: number;
  sticky?: StickyDirectionSnapState;
};

/**
 * Fold an angle into the bidirectional range `[0, π)`.
 */
export const normalizeBidirectionalAngleRadians = (radians: number): number => {
  let angle = radians % Math.PI;
  if (angle < 0) angle += Math.PI;
  return angle;
};

/**
 * Collect unique edge directions (plus world H/V) from shape polylines.
 * Angles are bidirectional in `[0, π)`.
 */
export const collectBidirectionalEdgeAnglesRadians = (
  shapes: readonly { points?: readonly Point2D[] | null | undefined }[],
): number[] => {
  const angles: number[] = [];
  const seen = new Set<number>();

  const push = (radians: number) => {
    const angle = normalizeBidirectionalAngleRadians(radians);
    // Quantize so near-duplicates collapse.
    const key = Math.round(angle * 1000);
    if (seen.has(key)) return;
    seen.add(key);
    angles.push(angle);
  };

  push(0);
  push(Math.PI / 2);

  for (const shape of shapes) {
    const points = shape.points;
    if (!points || points.length < 2) continue;

    const closed = points.length >= 3;
    const count = closed ? points.length : points.length - 1;
    for (let i = 0; i < count; i += 1) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      if (!a || !b) continue;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      if (dx === 0 && dy === 0) continue;
      push(Math.atan2(dy, dx));
    }
  }

  return angles;
};

/**
 * Soft-project {@link tip} onto lines through {@link origin} along given directions.
 * Once locked, requires {@link SoftSnapOffsetToDirectionsOptions.breakoutDistanceMeters}
 * of perpendicular travel before leaving that direction (Figma-like hysteresis).
 */
export const softSnapOffsetToDirections = (
  origin: Point2D,
  tip: Point2D,
  directionRadians: readonly number[],
  options: SoftSnapOffsetToDirectionsOptions,
): SoftSnapOffsetToDirectionsResult => {
  const { enterDistanceMeters, breakoutDistanceMeters, sticky } = options;
  if (!(enterDistanceMeters > 0) || !Number.isFinite(enterDistanceMeters)) {
    return { position: tip, snapped: false, state: {} };
  }

  const projectOntoDirection = (angleRadians: number): Point2D => {
    const ux = Math.cos(angleRadians);
    const uy = Math.sin(angleRadians);
    const dx = tip[0] - origin[0];
    const dy = tip[1] - origin[1];
    const t = dx * ux + dy * uy;
    return [origin[0] + t * ux, origin[1] + t * uy];
  };

  const locked = sticky?.lockedAngleRadians;
  if (locked !== undefined) {
    const projected = projectOntoDirection(locked);
    if (getDistance(tip, projected) <= breakoutDistanceMeters) {
      return {
        position: projected,
        snapped: true,
        angleRadians: locked,
        state: { lockedAngleRadians: locked },
      };
    }
  }

  let best:
    { angleRadians: number; projected: Point2D; distance: number } | undefined;

  for (const rawAngle of directionRadians) {
    const angleRadians = normalizeBidirectionalAngleRadians(rawAngle);
    const projected = projectOntoDirection(angleRadians);
    const distance = getDistance(tip, projected);
    if (distance > enterDistanceMeters) continue;
    if (!best || distance < best.distance) {
      best = { angleRadians, projected, distance };
    }
  }

  if (!best) {
    return { position: tip, snapped: false, state: {} };
  }

  return {
    position: best.projected,
    snapped: true,
    angleRadians: best.angleRadians,
    state: { lockedAngleRadians: best.angleRadians },
  };
};
